<template>
  <div>


    <div class="box">

      <div class="flex flex-wrap items-center">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getCustomers" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex flex-wrap">
          <div
            v-if="hasPermission('customer.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="$router.push('/customer/create/')"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New customer</span>
          </div>

          <button
            v-if="hasPermission('customer.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.customer.enabled"
            @click="exportCustomers"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.customer.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getCustomers">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>



      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <customer-filters
            :filters.sync="filters"
            @updated="getCustomers"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasCustomers">No data to display</p>
          <div class="overflow-x-auto" v-else>
            <table class="table table-centered table-hover table-striped mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Created at</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(customer, i) in customers" :key="i">
                  <td>{{ customer.id }}</td>
                  <td>{{ customer.first_name }} {{ customer.last_name }}</td>
                  <td>{{ customer.email }}</td>
                  <td>{{ $moment(customer.created_at).format(config.dateTimeFormats.display) }}</td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/customer/details/' + customer.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        v-if="hasPermission('customer.edit')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/customer/edit/' + customer.id)"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        v-if="hasPermission('customer.delete')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteCustomer(customer.id)"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="w-full flex justify-center">
            <pagination-component
              class="mt-8"
              v-if="hasCustomers && pagination.totalRows / pagination.perPage > 1"
              v-model="pagination.currentPage"
              :total-rows="pagination.totalRows"
              :rows-per-page="pagination.perPage"
            />
          </div>

        </div>
      </div>

    </div>


  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const CustomerFilters = resolve => require(['@/components/filters/Filters'], resolve)
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  components: {
    FilteringToggleButton,
    CustomerFilters,
    ListSearch,
    PaginationComponent,
  },
  data() {
    return {
      customers: [],
      searchInput: '',
      query: {
        search: '',
      },
      exportFile: {
        customer: {
          enabled: true,
        }
      },
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getCustomers()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getCustomers()
      }
    }
  },
  computed: {
    hasCustomers() {
      return Object.entries(this.customers).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.getCustomers()
  },
  methods: {
    exportCustomers() {
      this.exportFile.customer.enabled = false
      axios.get('customers/export', {
        params: {
          search: this.searchInput
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.customer.enabled = true
        })
    },
    deleteCustomer(customerID) {
      this.$swal.fire({
        title: 'Delete customer',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('customers/' + customerID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getCustomers()
              })
          }
        })
    },
    getCustomers() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      axios.get('customers', {params:{
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.customers = Object.assign({}, res.data)

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
